import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Clock, FileText } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { LeadForm } from './LeadForm';
const STORAGE_KEY = 'ase-lead-popup-seen';
// Shows shortly after the visitor lands (or earlier if they scroll past the hero)
const DELAY_MS = 6000;
const SCROLL_TRIGGER = 0.2;
export function LeadFormModal() {
  const {
    t
  } = useLang();
  const m = t.leadPopup;
  const [open, setOpen] = useState(false);
  // Once the popup has been seen, a small button stays on screen to reopen it
  const [seen, setSeen] = useState(() => typeof window !== 'undefined' && sessionStorage.getItem(STORAGE_KEY) === '1');
  const [submitted, setSubmitted] = useState(false);

  // Auto-show once per session: after a delay or when the visitor scrolls past the hero
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const show = () => {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
      sessionStorage.setItem(STORAGE_KEY, '1');
      setSeen(true);
      setOpen(true);
      cleanup();
    };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > SCROLL_TRIGGER) show();
    };
    const timer = window.setTimeout(show, DELAY_MS);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
    return cleanup;
  }, []);

  // Escape to close + lock background scroll
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);
  return <>
    <AnimatePresence data-id="element-274" data-mp="27cyplqvnlk8s">
      {seen && !open && !submitted && <motion.button type="button" onClick={() => setOpen(true)} initial={{
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 16,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed bottom-5 left-5 sm:bottom-7 sm:left-7 z-40 inline-flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-gold text-dark text-sm font-bold whitespace-nowrap shadow-[0_8px_24px_rgba(0,0,0,0.45)] hover:bg-gold-400 transition-colors" data-id="element-275" data-mp="2cd35pfsngcyh">
          <FileText className="w-4 h-4" aria-hidden="true" data-id="element-276" data-mp="2had61qhku913" />
          {m.button}
        </motion.button>}
    </AnimatePresence>
    <AnimatePresence data-id="element-277" data-mp="3a0bnp0tfz3vq">
      {open && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/75 backdrop-blur-sm" onClick={() => setOpen(false)} role="dialog" aria-modal="true" aria-labelledby="lead-popup-title" data-id="element-278" data-mp="39za9wol1k3ab">
          <motion.div initial={{
          opacity: 0,
          y: 24,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 24,
          scale: 0.96
        }} transition={{
          duration: 0.28,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={e => e.stopPropagation()} className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-dark-100 border border-gold/30 rounded-t-3xl sm:rounded-3xl shadow-2xl" data-id="element-279" data-mp="3sxdjl2j64dko">
            <button onClick={() => setOpen(false)} aria-label={t.legal.close} className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors" data-id="element-280" data-mp="3a8p4ix7d92gt">
              <X className="w-5 h-5" data-id="element-281" data-mp="2v6s0k37qysge" />
            </button>

            <div className="px-6 pt-8 pb-6 sm:px-8 border-b border-white/10" data-id="element-282" data-mp="304tigktfslqa">
              <div className="inline-flex items-center gap-2 text-gold text-sm font-bold mb-3" data-id="element-283" data-mp="352buwl6nxj31">
                <Clock className="w-4 h-4" data-id="element-284" data-mp="394yddvhg1rnk" />
                {m.badge}
              </div>
              <h2 id="lead-popup-title" className="text-2xl sm:text-3xl font-bold text-white leading-tight pr-8" data-id="element-285" data-mp="3e3t5xp57qukv">
                {m.titleA} <span className="text-gold" data-id="element-286" data-mp="3j0jku95a7k4p">{m.titleHighlight}</span>
              </h2>
              <p className="mt-3 text-gray-400" data-id="element-287" data-mp="3nu4lk8y9es7l">{m.subtitle}</p>
            </div>

            <div className="px-6 py-6 sm:px-8" data-id="element-288" data-mp="3sr4jtxovaqo5">
              <LeadForm idPrefix="popup" compact onDone={() => {
              setSubmitted(true);
              setOpen(false);
            }} data-id="element-289" data-mp="on6v1r1my71" />
              <button type="button" onClick={() => setOpen(false)} className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-300 transition-colors" data-id="element-290" data-mp="1q6aaj8fxtj85">
                {m.dismiss}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>
    </>;
}